<template>
    <v-container fluid class="pa-4">

        <!-- Sección 1: Configuración de usuario -->
        <v-card class="mb-4" outlined>
            <v-card-title class="text-subtitle-1 font-weight-medium">
                <v-icon left color="primary">mdi-account-cog</v-icon>
                {{ $t('userConfiguration.sections.userTitle') }}
            </v-card-title>
            <v-divider />
            <v-card-text>
                <v-row align="center">
                    <v-col cols="12" md="3">
                        <v-select
                            v-model="form.depositoIdSeleccionado"
                            :items="depositos"
                            item-text="nombre"
                            item-value="id"
                            :label="$t('userConfiguration.labels.deposito')"
                            :loading="loadingDepositos"
                            :disabled="loading"
                            clearable
                            prepend-icon="mdi-warehouse"
                            hide-details
                        >
                            <template v-slot:item="{ item }">
                                <v-list-item-content>
                                    <v-list-item-title>{{ item.nombre }}</v-list-item-title>
                                    <v-list-item-subtitle v-if="item.direccion">{{ item.direccion }}</v-list-item-subtitle>
                                </v-list-item-content>
                            </template>
                        </v-select>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions class="px-4 pb-3">
                <v-btn color="primary" small :loading="loading" :disabled="loading" @click="save">
                    {{ $t('userConfiguration.messages.save') }}
                </v-btn>
            </v-card-actions>
        </v-card>

        <!-- Sección 2: Logo PDF empresa -->
        <v-card v-if="canManageCompanyPdfLogo" class="mb-4" outlined>
            <v-card-title class="text-subtitle-1 font-weight-medium">
                <v-icon left color="primary">mdi-domain</v-icon>
                {{ $t('userConfiguration.sections.companyTitle') }}
            </v-card-title>
            <v-divider />
            <v-card-text>
                <v-row align="center">
                    <v-col cols="12" md="3">
                        <v-file-input
                            v-model="pdfLogoFileModel"
                            accept="image/png,image/jpeg,image/webp"
                            :label="$t('userConfiguration.company.pdfLogoLabel')"
                            prepend-icon="mdi-image-outline"
                            show-size
                            dense
                            hide-details
                            :disabled="loadingPdfLogo || savingPdfLogo"
                        />
                        <div class="text-caption text--secondary mt-1">
                            <span v-if="showLogoStoredInDb">
                                <v-icon x-small color="success">mdi-check-circle-outline</v-icon>
                                {{ $t('userConfiguration.company.logoStoredMessage') }}
                            </span>
                            <span v-else-if="showLogoReplaceHint">{{ $t('userConfiguration.company.logoReplaceHint') }}</span>
                        </div>
                    </v-col>
                    <v-col cols="12" md="3" class="d-flex align-center">
                        <div v-if="displayLogoPreview" class="pdf-logo-preview-wrap pa-1">
                            <img :src="displayLogoPreview" alt="PDF logo" class="pdf-logo-preview-img" />
                        </div>
                        <span v-else-if="!loadingPdfLogo" class="text-caption text--disabled">{{ $t('userConfiguration.company.noPreview') }}</span>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions class="px-4 pb-3">
                <v-btn color="primary" small :loading="savingPdfLogo" :disabled="!pendingBase64 || loadingPdfLogo || savingPdfLogo" @click="savePdfLogo">
                    {{ $t('userConfiguration.messages.save') }}
                </v-btn>
            </v-card-actions>
        </v-card>

        <!-- Sección 3: Documentos -->
        <v-card v-if="canManageCompanyConfiguration" class="mb-4" outlined>
            <v-card-title class="text-subtitle-1 font-weight-medium">
                <v-icon left color="primary">mdi-file-document-outline</v-icon>
                {{ $t('userConfiguration.companyConfig.sectionTitle') }}
            </v-card-title>
            <v-divider />
            <v-card-text>
                <v-checkbox
                    v-model="mostrarPrecioEnRemito"
                    :label="$t('userConfiguration.companyConfig.mostrarPrecioEnRemitoLabel')"
                    :disabled="loadingCompanyConfig || savingCompanyConfig"
                    hide-details
                    class="mt-0"
                />
            </v-card-text>
            <v-card-actions class="px-4 pb-3">
                <v-btn color="primary" small :loading="savingCompanyConfig" :disabled="loadingCompanyConfig || savingCompanyConfig" @click="saveCompanyConfiguration">
                    {{ $t('userConfiguration.messages.save') }}
                </v-btn>
            </v-card-actions>
        </v-card>

        <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
            {{ snackbarText }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="snackbar = false">
                    Close
                </v-btn>
            </template>
        </v-snackbar>
    </v-container>
</template>

<script>
import { InputErrorsByProps } from '@dracul/common-frontend'
import { mapGetters, mapState } from 'vuex'
import UserConfigurationProvider from '../../../providers/UserConfigurationProvider'
import CompanyPdfLogoProvider from '../../../providers/CompanyPdfLogoProvider'
import CompanyConfigurationProvider from '../../../providers/CompanyConfigurationProvider'
import DepositoProvider from '../../../../deposito/providers/DepositoProvider'

export default {
    name: "UserConfigurationManagement",
    mixins: [InputErrorsByProps],
    data() {
        return {
            form: {
                depositoIdSeleccionado: null
            },
            depositos: [],
            loading: false,
            loadingDepositos: false,
            loadingPdfLogo: false,
            savingPdfLogo: false,
            pdfLogoFileModel: null,
            localPreviewDataUrl: null,
            pendingBase64: null,
            pendingMimeType: null,
            mostrarPrecioEnRemito: true,
            loadingCompanyConfig: false,
            savingCompanyConfig: false,
            snackbar: false,
            snackbarText: '',
            snackbarColor: 'success'
        }
    },
    computed: {
        ...mapGetters(['me']),
        ...mapState('companyBranding', {
            brandingHasPdfLogo: 'hasPdfLogo',
            brandingLoaded: 'loaded',
        }),
        canManageCompanyPdfLogo() {
            const perms = this.me && this.me.role && this.me.role.permissions
            if (!perms) return false
            return perms.includes('COMPANY_PDF_LOGO_UPDATE') && perms.includes('COMPANY_PDF_LOGO_SHOW')
        },
        canManageCompanyConfiguration() {
            const perms = this.me && this.me.role && this.me.role.permissions
            return !!(perms && perms.includes('USER_CONFIGURATION_UPDATE'))
        },
        displayLogoPreview() {
            if (this.localPreviewDataUrl) return this.localPreviewDataUrl
            const u = this.$store.state.companyBranding && this.$store.state.companyBranding.pdfLogoDataUrl
            return u || null
        },
        showLogoStoredInDb() {
            return this.brandingLoaded && this.brandingHasPdfLogo && !this.loadingPdfLogo && !this.localPreviewDataUrl
        },
        showLogoReplaceHint() {
            return this.brandingLoaded && this.brandingHasPdfLogo && !!this.localPreviewDataUrl
        },
        previewCaption() {
            if (this.localPreviewDataUrl) return this.$t('userConfiguration.company.previewNewFile')
            if (this.brandingHasPdfLogo && this.displayLogoPreview) return this.$t('userConfiguration.company.previewSaved')
            return this.$t('userConfiguration.company.preview')
        },
    },
    mounted() {
        this.loadUserConfiguration().then((config) => {
            this.loadDepositos(config)
        })
        if (this.canManageCompanyPdfLogo) {
            this.ensureCompanyPdfLogoLoaded()
        }
        if (this.canManageCompanyConfiguration) {
            this.loadCompanyConfiguration()
        }
    },
    watch: {
        canManageCompanyPdfLogo(val) {
            if (val) this.ensureCompanyPdfLogoLoaded()
        },
        canManageCompanyConfiguration(val) {
            if (val) this.loadCompanyConfiguration()
        },
        pdfLogoFileModel(val) {
            this.processPdfLogoFile(val)
        },
    },
    methods: {
        loadCompanyConfiguration() {
            this.loadingCompanyConfig = true
            CompanyConfigurationProvider.get()
                .then((response) => {
                    const data = response && response.data && response.data.companyConfigurationGet
                    if (data) {
                        this.mostrarPrecioEnRemito = data.mostrarPrecioEnRemito !== false
                    }
                })
                .catch((error) => {
                    console.error('Error loading company configuration:', error)
                    this.showSnackbar(this.$t('userConfiguration.companyConfig.errorLoad'), 'error')
                })
                .finally(() => {
                    this.loadingCompanyConfig = false
                })
        },
        saveCompanyConfiguration() {
            this.savingCompanyConfig = true
            CompanyConfigurationProvider.update({
                input: { mostrarPrecioEnRemito: this.mostrarPrecioEnRemito }
            })
                .then(() => {
                    this.showSnackbar(this.$t('userConfiguration.companyConfig.saved'), 'success')
                })
                .catch((error) => {
                    console.error('Error saving company configuration:', error)
                    this.showSnackbar(this.$t('userConfiguration.companyConfig.errorSave'), 'error')
                })
                .finally(() => {
                    this.savingCompanyConfig = false
                })
        },
        ensureCompanyPdfLogoLoaded() {
            if (!this.canManageCompanyPdfLogo) return
            if (this.$store.state.companyBranding.loaded) return
            this.loadingPdfLogo = true
            CompanyPdfLogoProvider.get()
                .then((response) => {
                    const payload = response && response.data && response.data.companyPdfLogoGet
                    if (payload) {
                        this.$store.commit('companyBranding/setPdfLogoFromPayload', payload)
                    }
                })
                .catch((error) => {
                    console.error('Error loading company PDF logo:', error)
                    this.showSnackbar(this.$t('userConfiguration.company.errorLoad'), 'error')
                })
                .finally(() => {
                    this.loadingPdfLogo = false
                })
        },
        processPdfLogoFile(val) {
            this.localPreviewDataUrl = null
            this.pendingBase64 = null
            this.pendingMimeType = null
            const file = Array.isArray(val) ? (val[0] || null) : val
            if (!file || typeof file !== 'object') return
            const isBlob = typeof Blob !== 'undefined' && file instanceof Blob
            if (!isBlob) return
            const reader = new FileReader()
            reader.onload = () => {
                const dataUrl = reader.result
                if (typeof dataUrl !== 'string') return
                this.localPreviewDataUrl = dataUrl
                const marker = ';base64,'
                const b64Idx = dataUrl.indexOf(marker)
                if (b64Idx === -1) return
                this.pendingBase64 = dataUrl.slice(b64Idx + marker.length)
                const mimeFromUrl = dataUrl.slice(5, dataUrl.indexOf(';'))
                let mime = (mimeFromUrl || file.type || '').split(';')[0].trim().toLowerCase()
                if (mime === 'image/jpg') mime = 'image/jpeg'
                this.pendingMimeType = mime
                this.$forceUpdate()
            }
            reader.onerror = () => {
                this.showSnackbar(this.$t('userConfiguration.company.errorReadFile'), 'error')
            }
            reader.readAsDataURL(file)
        },
        savePdfLogo() {
            if (!this.pendingBase64 || !this.pendingMimeType) {
                this.showSnackbar(this.$t('userConfiguration.company.pickFileFirst'), 'warning')
                return
            }
            this.savingPdfLogo = true
            CompanyPdfLogoProvider.update({
                input: {
                    base64: this.pendingBase64,
                    mimeType: this.pendingMimeType,
                },
            })
                .then((response) => {
                    const payload = response && response.data && response.data.companyPdfLogoUpdate
                    if (payload) {
                        this.$store.commit('companyBranding/setPdfLogoFromPayload', payload)
                        this.clearPendingLogoFile()
                        this.showSnackbar(this.$t('userConfiguration.company.savedLogo'), 'success')
                    }
                })
                .catch((error) => {
                    console.error('Error saving company PDF logo:', error)
                    const ClientError = require('@dracul/common-frontend').ClientError
                    const clientError = new ClientError(error)
                    this.inputErrors = clientError.inputErrors
                    this.showSnackbar(this.$t('userConfiguration.company.errorSave'), 'error')
                })
                .finally(() => {
                    this.savingPdfLogo = false
                })
        },
        clearPendingLogoFile() {
            this.pdfLogoFileModel = null
            this.localPreviewDataUrl = null
            this.pendingBase64 = null
            this.pendingMimeType = null
        },
        loadDepositos(userConfig = null) {
            this.loadingDepositos = true

            const processConfig = (config) => {
                if (!config) {
                    DepositoProvider.fetchDepositos()
                        .then((response) => {
                            if (response && response.data && response.data.depositoFetch) {
                                this.depositos = response.data.depositoFetch.map(deposito => ({
                                    ...deposito,
                                    id: String(deposito.id)
                                }))
                            }
                        })
                        .catch((error) => {
                            console.error('Error loading depositos:', error)
                            this.showSnackbar(this.$t('userConfiguration.messages.errorLoadingDepositos'), 'error')
                        })
                        .finally(() => {
                            this.loadingDepositos = false
                        })
                    return
                }

                const depositoIds = config.depositoIds || []

                if (config.depositos && config.depositos.length > 0) {
                    this.depositos = config.depositos.map(deposito => ({
                        ...deposito,
                        id: String(deposito.id)
                    }))
                    this.loadingDepositos = false
                } else if (depositoIds.length > 0) {
                    DepositoProvider.fetchDepositos()
                        .then((response) => {
                            if (response && response.data && response.data.depositoFetch) {
                                const depositoIdsStr = depositoIds.map(id => String(id))
                                this.depositos = response.data.depositoFetch
                                    .filter(deposito => depositoIdsStr.includes(String(deposito.id)))
                                    .map(deposito => ({
                                        ...deposito,
                                        id: String(deposito.id)
                                    }))
                            }
                        })
                        .catch((error) => {
                            console.error('Error loading depositos:', error)
                            this.showSnackbar(this.$t('userConfiguration.messages.errorLoadingDepositos'), 'error')
                        })
                        .finally(() => {
                            this.loadingDepositos = false
                        })
                } else {
                    this.depositos = []
                    this.loadingDepositos = false
                }
            }

            if (userConfig) {
                processConfig(userConfig)
            } else {
                UserConfigurationProvider.getUserConfiguration(false)
                    .then((userConfigResponse) => {
                        const config = userConfigResponse?.data?.userConfigurationGet || null
                        processConfig(config)
                    })
                    .catch((error) => {
                        console.error('Error loading user configuration for depositos:', error)
                        processConfig(null)
                    })
            }
        },
        loadUserConfiguration(forceRefresh = false) {
            this.loading = true
            return UserConfigurationProvider.getUserConfiguration(forceRefresh)
                .then((response) => {
                    if (response && response.data && response.data.userConfigurationGet) {
                        const config = response.data.userConfigurationGet
                        const depositoId = config.depositoIdSeleccionado ? String(config.depositoIdSeleccionado) : null
                        this.form.depositoIdSeleccionado = depositoId
                        return this.$nextTick().then(() => config)
                    }
                    return null
                })
                .catch((error) => {
                    console.error('Error loading user configuration:', error)
                    return null
                })
                .finally(() => {
                    this.loading = false
                })
        },
        save() {
            this.loading = true
            const depositoIdToSave = this.form.depositoIdSeleccionado || null
            UserConfigurationProvider.updateUserConfiguration({
                input: {
                    depositoIdSeleccionado: depositoIdToSave
                }
            })
                .then((response) => {
                    if (response && response.data && response.data.userConfigurationUpdate) {
                        this.showSnackbar(this.$t('userConfiguration.messages.saved'), 'success')
                        this.$nextTick(() => {
                            const idToSet = depositoIdToSave ? String(depositoIdToSave) : null
                            this.form.depositoIdSeleccionado = idToSet
                            this.$forceUpdate()
                        })
                        this.$root.$emit('userConfigurationUpdated')
                    }
                })
                .catch((error) => {
                    console.error('Error saving user configuration:', error)
                    let clientError = new (require('@dracul/common-frontend').ClientError)(error)
                    this.inputErrors = clientError.inputErrors
                    this.showSnackbar(this.$t('userConfiguration.messages.error'), 'error')
                })
                .finally(() => {
                    this.loading = false
                })
        },
        showSnackbar(text, color = 'success') {
            this.snackbarText = text
            this.snackbarColor = color
            this.snackbar = true
        }
    }
}
</script>

<style scoped>
.pdf-logo-preview-wrap {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
}
.pdf-logo-preview-img {
    max-height: 48px;
    max-width: 140px;
    object-fit: contain;
    display: block;
}
</style>
